import {
  ViolationsResponse,
  SuspensionsResponse,
  AppealsResponse,
  StatsResponse,
  WorkerOverviewResponse,
  ViolationFilters,
  SuspensionFilters,
} from '../types/accountability';
import { axiosRubyClient as api } from '@/util/axios/axiosClient';

export const accountabilityAPI = {
  // Get violations list with filters
  getViolations: async (
    filters: ViolationFilters,
  ): Promise<ViolationsResponse> => {
    const response = await api.get('/api/admin/accountability/violations', {
      params: filters,
    });
    return response.data;
  },

  // Get suspensions list with filters
  getSuspensions: async (
    filters: SuspensionFilters,
  ): Promise<SuspensionsResponse> => {
    const response = await api.get('/api/admin/accountability/suspensions', {
      params: filters,
    });
    return response.data;
  },

  // Get pending appeals
  getPendingAppeals: async (
    page = 1,
    perPage = 25,
  ): Promise<AppealsResponse> => {
    const response = await api.get(
      '/api/admin/accountability/appeals/pending',
      {
        params: { page, per_page: perPage },
      },
    );
    return response.data;
  },

  // Get system statistics
  getStats: async (
    startDate?: string,
    endDate?: string,
  ): Promise<StatsResponse> => {
    const response = await api.get('/api/admin/accountability/stats', {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  // Get worker accountability overview
  getWorkerOverview: async (
    workerId: number,
  ): Promise<WorkerOverviewResponse> => {
    const response = await api.get(
      `/api/admin/workers/${workerId}/accountability`,
    );
    return response.data;
  },

  // Forgive a violation
  forgiveViolation: async (
    violationId: number,
    reason: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/forgive`,
      {
        reason,
      },
    );
    return response.data;
  },

  // Remove a violation
  removeViolation: async (
    violationId: number,
    reason: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/remove`,
      {
        reason,
      },
    );
    return response.data;
  },

  // Lift a suspension
  liftSuspension: async (
    suspensionId: number,
    reason: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/suspensions/${suspensionId}/lift`,
      {
        reason,
      },
    );
    return response.data;
  },

  // Approve an appeal
  approveAppeal: async (
    violationId: number,
    notes: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/appeals/${violationId}/approve`,
      {
        admin_notes: notes,
      },
    );
    return response.data;
  },

  // Deny an appeal
  denyAppeal: async (
    violationId: number,
    notes: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/appeals/${violationId}/deny`,
      {
        admin_notes: notes,
      },
    );
    return response.data;
  },

  // Update violation notes
  updateNotes: async (
    violationId: number,
    notes: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(
      `/api/admin/accountability/violations/${violationId}/update_notes`,
      {
        admin_notes: notes,
      },
    );
    return response.data;
  },
};
